import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  automationService,
  type Strategy,
  type Scenario,
  type ScenarioSummary,
  type StrategyTrade,
  tradeChartable,
} from '@/services/strategyService';
import { EquityCurveChart, PnLDistributionChart, MfeScatterPlot } from './PerformanceCharts';
import { TradeChartDialog } from '@/components/trading/TradeChartDialog';
import {
  Pagination, usePagedList, useEmptyPageGuard, useDebounced, useLatestRequest,
} from '@/components/ui/pagination';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { money, pct, ratio, fixed, duration, dateOnly } from '@/lib/format';
import { SearchInput, FilterChips, SortControl, EmptyState, type FilterOption } from '@/components/ui/list-toolbar';
import {
  Play,
  Trash2,
  BarChart3,
  AlertCircle,
  Loader2,
  CandlestickChart,
} from 'lucide-react';

type ScenarioStatusFilter = 'all' | 'completed' | 'profitable';

const SCENARIO_STATUS_OPTIONS: FilterOption<ScenarioStatusFilter>[] = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'profitable', label: 'Profitable', activeClass: 'bg-emerald-500 text-white' },
];

const SCENARIO_SORT_OPTIONS = [
  { value: 'createdAt', label: 'Newest' },
  { value: 'totalPnL', label: 'Total P&L' },
  { value: 'winRate', label: 'Win rate' },
  { value: 'profitFactor', label: 'Profit factor' },
  { value: 'tradeCount', label: 'Trade count' },
  { value: 'name', label: 'Name' },
] as const;

/** One column on a phone, filling out to four on a wide desktop. */
const GRID = 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4';

export const ScenarioBacktestPage: React.FC = () => {
  const [rules, setRules] = useState<Strategy[]>([]);
  // List endpoint returns lightweight summaries (no trades). Full Scenario is
  // lazy-fetched into selectedScenarioFull when the user selects one.
  const [scenarios, setScenarios] = useState<ScenarioSummary[]>([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
  const [selectedScenarioFull, setSelectedScenarioFull] = useState<Scenario | null>(null);
  // Kept separate from the page-level `error` banner: the detail now opens in a
  // modal, and a banner rendered behind it is a banner nobody sees. Without this a
  // failed fetch left the dialog on "Loading results…" forever.
  const [detailError, setDetailError] = useState<string | null>(null);
  // Per-trade chart — same dialog the Backtest Lab and strategy panel use.
  const [chartTrade, setChartTrade] = useState<StrategyTrade | null>(null);
  const [selectedRuleId, setSelectedRuleId] = useState<string>('');
  const [lookbackDays, setLookbackDays] = useState(90);
  const [symbolsInput, setSymbolsInput] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Server-paged scenario list ──
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ScenarioStatusFilter>('all');
  const [sortKey, setSortKey] = useState<string>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const debouncedSearch = useDebounced(search, 300);
  const { page, setPage, pageSize, setPageSize } = usePagedList({
    pageSize: 25,
    resetOn: [debouncedSearch, statusFilter, sortKey, sortDir],
  });

  const beginScenarioRequest = useLatestRequest();

  const loadScenarios = useCallback(async () => {
    const isCurrent = beginScenarioRequest();
    setListLoading(true);
    try {
      // Sorting and filtering happen in the DB, so "best run" means best across every
      // scenario, not merely the best on the page in front of you.
      const result = await automationService.listScenariosPaged({
        limit: pageSize,
        offset: (page - 1) * pageSize,
        sortKey,
        sortDir,
        search: debouncedSearch || undefined,
        completedOnly: statusFilter === 'completed',
        profitableOnly: statusFilter === 'profitable',
      });
      // Discard a superseded page - see useLatestRequest.
      if (!isCurrent()) return;
      setScenarios(result.items);
      setTotal(result.total);
      setError(null);
    } catch (e: any) {
      if (!isCurrent()) return;
      setError(e.message);
    } finally {
      if (isCurrent()) setListLoading(false);
    }
  }, [page, pageSize, sortKey, sortDir, debouncedSearch, statusFilter, beginScenarioRequest]);

  useEffect(() => { void loadScenarios(); }, [loadScenarios]);

  // Rules only. The scenario list has its own effect keyed on the paging state, so
  // fetching it here as well meant two identical requests on every mount.
  // Deleting the last scenario on a page must not strand the view on an empty one.
  useEmptyPageGuard(page, setPage, scenarios.length, total);

  const load = useCallback(async () => {
    try {
      const r = await automationService.getRules();
      setRules(r);
      if (r.length > 0 && !selectedRuleId) setSelectedRuleId(r[0].id);
    } catch (e: any) {
      setError(e.message);
    }
  }, [selectedRuleId]);

  useEffect(() => { load(); }, [load]);

  // Lazy-fetch the full scenario (with trades) only when one is selected.
  useEffect(() => {
    if (!selectedScenarioId) { setSelectedScenarioFull(null); setDetailError(null); return; }
    let cancelled = false;
    setDetailError(null);
    automationService.getScenario(selectedScenarioId)
      .then(s => { if (!cancelled) setSelectedScenarioFull(s); })
      .catch(e => { if (!cancelled) setDetailError(e?.message || 'Failed to load this scenario.'); });
    return () => { cancelled = true; };
  }, [selectedScenarioId]);

  const runScenario = async () => {
    if (!selectedRuleId) return;
    setRunning(true);
    setError(null);
    try {
      const symbols = symbolsInput.trim()
        ? symbolsInput.split(',').map(s => s.trim().toUpperCase()).filter(Boolean)
        : undefined;
      const result = await automationService.runScenario(selectedRuleId, lookbackDays, symbols);
      setSelectedScenarioId(result.id);
      // Re-query rather than splicing the new run in: the list is server-sorted, and
      // prepending would put it somewhere the ordering does not actually place it.
      await loadScenarios();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const deleteScenario = async (id: string) => {
    if (!confirm('Delete this scenario?')) return;
    try {
      await automationService.deleteScenario(id);
      if (selectedScenarioId === id) setSelectedScenarioId(null);
      await loadScenarios();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // selectedScenario is the full Scenario (with trades) when loaded; falls back
  // to the summary while the lazy fetch is in flight so stats show immediately.
  // backtestSessionId moved into the nested backtest object in slice 4;
  // the summary already carries `backtest`, so we just spread it through.
  const selectedSummary = scenarios.find(s => s.id === selectedScenarioId);
  const selectedScenario: Scenario | null = selectedScenarioFull
    ?? (selectedSummary ? { ...selectedSummary, trades: [] } : null);
  const stats = selectedScenario?.statistics;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <BarChart3 className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Backtests</h2>
          <span className="text-xs text-muted-foreground">Run a trading rule over history and inspect every trade</span>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">{error}</div>
      )}

      {/* ── Run Form ── */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Trading Rule</label>
              <select
                className="h-9 px-3 rounded-md border bg-background text-sm min-w-[200px]"
                value={selectedRuleId}
                onChange={e => setSelectedRuleId(e.target.value)}
              >
                {rules.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              {/* The walkthrough's hardest dead-end: a new user lands here with an
                  empty dropdown and no way forward. Point them at the fix. */}
              {rules.length === 0 && (
                <div className="text-xs text-amber-500 mt-1">
                  A backtest runs a trading rule —{' '}
                  <a href="#/dashboard/automation" className="underline font-semibold hover:text-amber-400">
                    create your first rule
                  </a>{' '}
                  and come back.
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Lookback Days</label>
              <input
                type="number"
                className="h-9 w-24 px-3 rounded-md border bg-background text-sm"
                value={lookbackDays}
                onChange={e => setLookbackDays(Number(e.target.value))}
                min={1}
                max={365}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground block mb-1">Symbols (comma-separated, blank = all)</label>
              <input
                type="text"
                className="h-9 w-full px-3 rounded-md border bg-background text-sm"
                placeholder="AAPL, TSLA, NVDA"
                value={symbolsInput}
                onChange={e => setSymbolsInput(e.target.value)}
              />
            </div>
            <Button size="sm" onClick={runScenario} disabled={running || !selectedRuleId}>
              {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1" />}
              {running ? 'Running...' : 'Run Scenario'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scenarios get the full width; results open in a dialog on click. The old
          layout spent two thirds of the page on a panel that said "Select a
          scenario" until you did. */}
      <div className="space-y-3">
          <Card>
            <CardContent className="space-y-2 p-2.5">
              <SearchInput value={search} onChange={setSearch} placeholder="Search scenarios…" />
              <div className="flex flex-wrap items-center gap-2">
                <FilterChips value={statusFilter} options={SCENARIO_STATUS_OPTIONS} onChange={(v) => setStatusFilter(v)} />
                <SortControl
                  sortBy={sortKey}
                  sortDir={sortDir}
                  options={SCENARIO_SORT_OPTIONS as unknown as { value: string; label: string }[]}
                  onChange={(by, dir) => { setSortKey(by); setSortDir(dir); }}
                  className="ml-auto"
                />
              </div>
              {total > pageSize && (
                <Pagination
                  page={page} pageSize={pageSize} total={total}
                  onPageChange={setPage} compact loading={listLoading} itemLabel="scenarios"
                />
              )}
            </CardContent>
          </Card>

          {/* No inner max-height scroller any more: the page scrolls, so a tile grid
              is not trapped in a 540px window with its own scrollbar. */}
          <div className={GRID}>
            {scenarios.map(s => {
              const pnl = s.statistics?.totalPnL;
              const profitable = pnl != null && pnl >= 0;
              return (
                <Card
                  key={s.id}
                  className={`cursor-pointer border-l-[3px] transition-all hover:shadow-sm ${
                    pnl == null ? 'border-l-transparent'
                      : profitable ? 'border-l-emerald-500 bg-emerald-500/[0.03]'
                        : 'border-l-red-500 bg-red-500/[0.03]'
                  } ${selectedScenarioId === s.id ? 'ring-1 ring-primary' : 'hover:border-muted-foreground/30'}`}
                  onClick={() => setSelectedScenarioId(s.id)}
                >
                  <CardContent className="space-y-1.5 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <span className="truncate text-sm font-medium">{s.name}</span>
                      <div className="flex shrink-0 items-center gap-1">
                        <StatusBadge status={s.status} />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-destructive"
                          onClick={e => { e.stopPropagation(); deleteScenario(s.id); }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[10px] text-muted-foreground">
                        {dateOnly(s.backtest?.startDate)} → {dateOnly(s.backtest?.endDate)}
                        {' · '}{(s.tradeCount ?? 0).toLocaleString()} trades
                      </span>
                      {pnl != null && (
                        <span className={`text-sm font-semibold tabular-nums ${profitable ? 'text-emerald-500' : 'text-red-500'}`}>
                          {money(pnl)}
                        </span>
                      )}
                    </div>
                    {s.statistics && (
                      <div className="flex gap-3 text-[10px] text-muted-foreground tabular-nums">
                        <span>{s.statistics.winRate.toFixed(0)}% win</span>
                        <span>PF {ratio(s.statistics.profitFactor)}</span>
                        <span>{fixed(s.statistics.avgRMultiple)}R avg</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {scenarios.length === 0 && !listLoading && (
            <Card>
              <EmptyState
                title={search || statusFilter !== 'all' ? 'No scenarios match these filters' : 'No scenarios yet'}
                hint={search || statusFilter !== 'all'
                  ? 'Filters run across every scenario, not just this page.'
                  : 'Run your first one above.'}
              />
            </Card>
          )}

          {total > 0 && (
            <Card>
              <CardContent className="p-2.5">
                <Pagination
                  page={page} pageSize={pageSize} total={total}
                  onPageChange={setPage} onPageSizeChange={setPageSize}
                  loading={listLoading} itemLabel="scenarios"
                />
              </CardContent>
            </Card>
          )}
        </div>

      {/* Scenario results, on tile click. */}
      <Dialog
        open={!!selectedScenarioId}
        onOpenChange={(open) => { if (!open) setSelectedScenarioId(null); }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">
              {selectedScenario?.name ?? 'Scenario'}
            </DialogTitle>
          </DialogHeader>

          {detailError ? (
            <div className="space-y-3">
              <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-md">
                <AlertCircle className="h-4 w-4 inline mr-1" /> {detailError}
              </div>
              {/* The summary row is already in hand from the list, so a failed detail
                  fetch still shows something rather than an empty modal. */}
              {selectedSummary?.statistics && (
                <p className="text-xs text-muted-foreground">
                  Summary figures below come from the list; trades and trade-derived
                  charts need the detail request to succeed.
                </p>
              )}
            </div>
          ) : selectedScenario && stats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Total Trades" value={stats.totalTrades.toString()} />
                <StatCard label="Win Rate" value={pct(stats.winRate)} positive={stats.winRate >= 50} />
                <StatCard label="Total P&L" value={money(stats.totalPnL)} positive={stats.totalPnL >= 0} />
                <StatCard label="Profit Factor" value={fixed(stats.profitFactor)} positive={stats.profitFactor >= 1} />
                <StatCard label="Expectancy" value={money(stats.expectancy)} positive={stats.expectancy >= 0} />
                <StatCard label="Avg R-Multiple" value={fixed(stats.avgRMultiple)} positive={stats.avgRMultiple >= 0} />
                <StatCard label="Max Drawdown" value={money(-stats.maxDrawdown)} positive={false} />
                <StatCard label="Avg Hold Time" value={duration(stats.avgHoldingSeconds)} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardHeader className="p-3 pb-1"><CardTitle className="text-sm">Best & Worst</CardTitle></CardHeader>
                  <CardContent className="p-3 pt-1 text-xs space-y-1">
                    <div className="text-green-500">Largest Win: {money(stats.largestWin)}</div>
                    <div className="text-green-500">Avg Win: {money(stats.avgWin)}</div>
                    <div className="text-red-500">Largest Loss: {money(stats.largestLoss)}</div>
                    <div className="text-red-500">Avg Loss: {money(stats.avgLoss)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="p-3 pb-1"><CardTitle className="text-sm">Rule Config</CardTitle></CardHeader>
                  <CardContent className="p-3 pt-1 text-xs space-y-1">
                    <div>Scanner: {selectedScenario.strategySnapshot?.scannerId || selectedScenario.scannerId}</div>
                    <div>Direction: {selectedScenario.strategySnapshot?.direction || '—'}</div>
                    <div>Sizing: {selectedScenario.strategySnapshot?.sizing?.mode} {selectedScenario.strategySnapshot?.sizing?.value}%</div>
                    <div>Symbols: {selectedScenario.symbols.length > 0 ? selectedScenario.symbols.join(', ') : 'All discovered'}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Equity Curve */}
              {stats.equityCurve.length > 0 && (
                <EquityCurveChart equityCurve={stats.equityCurve} />
              )}

              {/* P&L Distribution + MFE/MAE */}
              {selectedScenario.trades.length > 0 && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                  <PnLDistributionChart trades={selectedScenario.trades.filter(t => t.status === 'Closed')} />
                  <MfeScatterPlot trades={selectedScenario.trades.filter(t => t.status === 'Closed')} />
                </div>
              )}

              {/* Trade table */}
              {selectedScenario.trades.length > 0 && (
                <Card>
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-sm">Trades ({selectedScenario.trades.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-auto max-h-[400px]">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="p-2 text-left">Symbol</th>
                            <th className="p-2 text-left">Dir</th>
                            <th className="p-2 text-right">Entry</th>
                            <th className="p-2 text-right">Exit</th>
                            <th className="p-2 text-right">P&L</th>
                            <th className="p-2 text-right">R</th>
                            <th className="p-2 text-left">Reason</th>
                            <th className="p-2 text-right">Hold</th>
                            <th className="p-2 text-left">Time</th>
                            <th className="w-8 p-2" />
                          </tr>
                        </thead>
                        <tbody>
                          {selectedScenario.trades.map(t => (
                            <tr
                              key={t.id}
                              className={`border-b border-muted/20 hover:bg-muted/10 ${tradeChartable(t) ? 'cursor-pointer' : ''}`}
                              title={tradeChartable(t) ? 'View on chart' : undefined}
                              onClick={() => { if (tradeChartable(t)) setChartTrade(t); }}
                            >
                              <td className="p-2 font-medium">{t.symbol}</td>
                              <td className="p-2">{t.direction === 'Long' ? '▲' : '▼'}</td>
                              <td className="p-2 text-right">${fixed(t.entryPrice)}</td>
                              <td className="p-2 text-right">{t.exitPrice ? `$${fixed(t.exitPrice)}` : '—'}</td>
                              <td className={`p-2 text-right ${t.realizedPnL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {t.status === 'Closed' ? money(t.realizedPnL) : '—'}
                              </td>
                              <td className="p-2 text-right">{t.status === 'Closed' ? fixed(t.realizedRMultiple, 1) : '—'}</td>
                              <td className="p-2">{t.exitReason || '—'}</td>
                              <td className="p-2 text-right">{t.holdingSeconds > 0 ? duration(t.holdingSeconds) : '—'}</td>
                              <td className="p-2 text-[10px] text-muted-foreground">{new Date(t.entryTime).toLocaleString()}</td>
                              <td className="p-2">
                                {/* Real button so the chart is keyboard-reachable — the row
                                    click is a pointer convenience, not the only path. Only
                                    statuses that own a position chart (allowlist). */}
                                {tradeChartable(t) && (
                                  <button
                                    type="button"
                                    title="View on chart"
                                    className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                                    onClick={(e) => { e.stopPropagation(); setChartTrade(t); }}
                                  >
                                    <CandlestickChart className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : selectedScenario && selectedScenario.status === 'Running' ? (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Running scenario...
            </div>
          ) : selectedScenario && selectedScenario.status === 'Failed' ? (
            <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-md">
              <AlertCircle className="h-4 w-4 inline mr-1" /> {selectedScenario.error}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading results…
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* The trade's own write-once assetClass wins; the run's strategy snapshot
          covers pre-field legacy trades. The chart picks bar source + price
          precision from it. */}
      <TradeChartDialog
        trade={chartTrade
          ? ({ ...chartTrade, assetClass: chartTrade.assetClass ?? selectedScenario?.strategySnapshot?.assetClass ?? 'Equity' } as any)
          : null}
        events={chartTrade?.events}
        entryReason={chartTrade?.entryReason ?? null}
        onClose={() => setChartTrade(null)}
      />
    </div>
  );
};

// ── Helpers ──

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const variant = status === 'Completed' ? 'default' : status === 'Failed' ? 'destructive' : 'secondary';
  return <Badge variant={variant} className="text-[10px]">{status}</Badge>;
};

const StatCard: React.FC<{ label: string; value: string; positive?: boolean }> = ({ label, value, positive }) => (
  <Card>
    <CardContent className="p-3">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-lg font-bold ${positive === true ? 'text-green-500' : positive === false ? 'text-red-500' : ''}`}>
        {value}
      </div>
    </CardContent>
  </Card>
);
